import { db } from '../firebase';
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
  writeBatch,
  deleteField // Add deleteField here
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { format } from 'date-fns';
import { dailyLogService } from './dailyLogService';

// Helper function to get current user ID
const getCurrentUserId = () => {
  const auth = getAuth();
  return auth.currentUser?.uid;
};

// Cache object
const cache = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const invoiceService = {
  async softDeleteInvoice(userId, invoiceId, reason = '') {
    try {
      const invoiceRef = doc(db, 'invoices', invoiceId);
      const invoice = await getDoc(invoiceRef);

      if (!invoice.exists()) {
        throw new Error('Invoice not found');
      }

      const invoiceData = invoice.data();
      if (invoiceData.userId !== userId) {
        throw new Error('Unauthorized');
      }

      await updateDoc(invoiceRef, {
        status: 'deleted',
        deletedAt: Timestamp.now(),
        deletedBy: userId,
        deleteReason: reason
      });

      return true;
    } catch (error) {
      console.error('Error soft deleting invoice:', error);
      throw error;
    }
  },

  async restoreInvoice(userId, invoiceId) {
    try {
      const invoiceRef = doc(db, 'invoices', invoiceId);
      const invoice = await getDoc(invoiceRef);

      if (!invoice.exists()) {
        throw new Error('Invoice not found');
      }

      const invoiceData = invoice.data();
      if (invoiceData.userId !== userId) {
        throw new Error('Unauthorized');
      }

      await updateDoc(invoiceRef, {
        status: 'active',
        deletedAt: null,
        deletedBy: null,
        deleteReason: null
      });

      return true;
    } catch (error) {
      console.error('Error restoring invoice:', error);
      throw error;
    }
  },

  // Function to get the next invoice number
  async getNextInvoiceNumber(userId) {
    // ... existing code ...
  },

  // Function to save an invoice
  async saveInvoice(userId, invoiceData) {
    if (!userId) {
      throw new Error("User ID is required to save an invoice.");
    }
    if (!invoiceData) {
      throw new Error("Invoice data is required.");
    }

    try {
      const nextInvoiceNumber = await this.getNextInvoiceNumber(userId);
      
      // Add default payment status fields
      const fullInvoiceData = {
        ...invoiceData,
        userId: userId,
        invoiceNumber: nextInvoiceNumber,
        createdAt: serverTimestamp(), // Use server timestamp for creation date
        status: 'active', // Default status
        isPaid: false,    // Default payment status
        paidDate: null,   // Default paid date
      };

      const docRef = await addDoc(collection(db, 'invoices'), fullInvoiceData);
      console.log("Invoice saved with ID: ", docRef.id);

      // Update the last invoice number used
      const userSettingsRef = doc(db, 'userSettings', userId);
      await updateDoc(userSettingsRef, {
        lastInvoiceNumber: nextInvoiceNumber
      });

      return { id: docRef.id, ...fullInvoiceData, invoiceNumber: nextInvoiceNumber };
    } catch (error) {
      console.error("Error saving invoice: ", error);
      throw error; // Re-throw the error for handling in the component
    }
  },

  // Function to get user profile data (potentially needed for PDF generation context)
  async getUserProfile(userId) {
    // ... existing code ...
  },

  // UPDATED Function to mark an invoice as paid - Now returns updated fields
  async markInvoicePaid(userId, invoiceId) {
    if (!userId || !invoiceId) {
      throw new Error("User ID and Invoice ID are required to mark as paid.");
    }
    try {
      const invoiceRef = doc(db, 'invoices', invoiceId);
      // Ensure the invoice belongs to the user (optional but good practice)
      const invoiceSnap = await getDoc(invoiceRef);
      if (!invoiceSnap.exists() || invoiceSnap.data().userId !== userId) {
        throw new Error("Invoice not found or access denied.");
      }

      const updateData = {
        isPaid: true,
        paidDate: serverTimestamp(), // Set paidDate to current server time
        status: 'paid', // Optionally update status field if used
        updatedAt: serverTimestamp()
      };

      await updateDoc(invoiceRef, updateData);
      console.log(`Invoice ${invoiceId} marked as paid.`);

      // Return the key updated fields (use current client time as approximation for paidDate)
      return { isPaid: true, paidDate: new Date() }; // Return confirmation

    } catch (error) {
      console.error("Error marking invoice as paid:", error);
      throw new Error(`Failed to mark invoice ${invoiceId} as paid.`);
    }
  },

  // UPDATED Function to mark an invoice as unpaid - Now returns updated fields
  async markInvoiceUnpaid(userId, invoiceId) {
    if (!userId || !invoiceId) {
      throw new Error("User ID and Invoice ID are required to mark as unpaid.");
    }
    try {
      const invoiceRef = doc(db, 'invoices', invoiceId);
      // Ensure the invoice belongs to the user (optional but good practice)
      const invoiceSnap = await getDoc(invoiceRef);
      if (!invoiceSnap.exists() || invoiceSnap.data().userId !== userId) {
        throw new Error("Invoice not found or access denied.");
      }

      const updateData = {
        isPaid: false,
        paidDate: deleteField(), // Remove the paidDate field
        status: 'unpaid', // Optionally update status field if used
        updatedAt: serverTimestamp()
      };

      await updateDoc(invoiceRef, updateData);
      console.log(`Invoice ${invoiceId} marked as unpaid.`);

      // Return the key updated fields
      return { isPaid: false, paidDate: null }; // Return confirmation

    } catch (error) {
      console.error("Error marking invoice as unpaid:", error);
      throw new Error(`Failed to mark invoice ${invoiceId} as unpaid.`);
    }
  },

  /**
   * Updates the status of a specific invoice.
   * @param {string} invoiceId - The ID of the invoice to update.
   * @param {string} newStatus - The new status ('paid', 'unpaid', 'deleted', etc.).
   * @returns {Promise<void>}
   */
  async updateInvoiceStatus(invoiceId, newStatus) {
    const userId = getCurrentUserId();
    if (!userId) {
      throw new Error("User not authenticated.");
    }
    if (!invoiceId) {
      throw new Error("Invoice ID is required.");
    }
    if (!newStatus) {
      throw new Error("New status is required.");
    }

    console.log(`[invoiceService] Updating status for invoice ${invoiceId} to ${newStatus} for user ${userId}`);
    const invoiceRef = doc(db, 'invoices', invoiceId);

    // Ensure the invoice belongs to the current user before updating (optional but good practice)
    // const docSnap = await getDoc(invoiceRef);
    // if (!docSnap.exists() || docSnap.data().userId !== userId) {
    //   throw new Error("Invoice not found or access denied.");
    // }

    const updateData = {
      status: newStatus,
      // If restoring from 'deleted', clear deletion fields
      ...(newStatus !== 'deleted' && {
        deletedAt: null,
        deletedBy: null,
        deleteReason: null
      }),
      // If setting to 'paid', update isPaid flag
      ...(newStatus === 'paid' && {
        isPaid: true
      }),
      // If setting to 'unpaid' or 'active' or restoring, ensure isPaid is false unless explicitly paid
      ...(newStatus !== 'paid' && {
        isPaid: false // Default to false when status is not 'paid'
      }),
      // Add a last updated timestamp?
      // lastUpdated: serverTimestamp() // Consider adding this
    };

    try {
      await updateDoc(invoiceRef, updateData);
      console.log(`[invoiceService] Successfully updated status for invoice ${invoiceId}`);

      // Invalidate relevant cache entries if caching is implemented for fetches
      // e.g., delete cache[`allInvoices_${userId}`];

    } catch (error) {
      console.error(`[invoiceService] Error updating invoice status for ${invoiceId}:`, error);
      throw error; // Re-throw the error for the calling function to handle
    }
  },

  /**
   * Fetches invoices for a user, optionally filtered and with cache control.
   * @param {string} userId - The ID of the user.
   * @param {object} [filters={}] - Optional filters (e.g., practice, startDate, endDate).
   * @param {object} [options={}] - Optional options (e.g., forceRefresh).
   * @returns {Promise<Array>} - A promise that resolves to an array of invoice objects.
   */
  async fetchFilteredInvoices(userId, filters = {}, options = {}) {
    if (!userId) {
      throw new Error("User ID is required to fetch invoices.");
    }

    const { practice, startDate, endDate } = filters;
    const { forceRefresh = false } = options; // Destructure forceRefresh option

    // --- Cache Key Generation ---
    // Ensure consistent key format, handle null/undefined dates
    const startKey = startDate ? new Date(startDate).toISOString() : 'null';
    const endKey = endDate ? new Date(endDate).toISOString() : 'null';
    const practiceKey = practice || 'all';
    const cacheKey = `allInvoices_${userId}_${practiceKey}_${startKey}_${endKey}`;

    // --- Cache Check ---
    // Bypass cache if forceRefresh is true
    if (!forceRefresh && cache[cacheKey] && (Date.now() - cache[cacheKey].timestamp < CACHE_DURATION)) {
      console.log(`[invoiceService/fetchFiltered] Returning cached invoice data for key: ${cacheKey}`);
      return cache[cacheKey].data;
    } else if (forceRefresh) {
       console.log(`[invoiceService/fetchFiltered] Force refresh requested, bypassing cache for key: ${cacheKey}`);
    } else if (cache[cacheKey]) {
       console.log(`[invoiceService/fetchFiltered] Cache expired for key: ${cacheKey}. Fetching fresh data.`);
    } else {
       console.log(`[invoiceService/fetchFiltered] No cache found for key: ${cacheKey}. Fetching fresh data.`);
    }


    // --- Firestore Query ---
    try {
      console.log(`[invoiceService/fetchFiltered] Fetching invoices from Firestore for user ${userId} with filters:`, filters);
      const invoicesRef = collection(db, 'invoices');
      let q = query(invoicesRef, where('userId', '==', userId));

      // Apply filters
      if (practice && practice !== 'all') {
        q = query(q, where('practiceId', '==', practice));
      }
      if (startDate) {
        q = query(q, where('createdAt', '>=', Timestamp.fromDate(new Date(startDate))));
      }
      if (endDate) {
        // Firestore includes the start date but excludes the end date in '<=' comparisons with timestamps if time is 00:00:00.
        // Ensure the endDate includes the whole day. If endDate is already ISO string end-of-day, it's fine.
        q = query(q, where('createdAt', '<=', Timestamp.fromDate(new Date(endDate))));
      }

      // Add default ordering
      q = query(q, orderBy('createdAt', 'desc'));

      const querySnapshot = await getDocs(q);
      const invoices = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Ensure date fields are consistently handled (e.g., convert Timestamps)
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : null,
        paidDate: doc.data().paidDate?.toDate ? doc.data().paidDate.toDate() : null,
        dueDate: doc.data().dueDate?.toDate ? doc.data().dueDate.toDate() : null, // Assuming dueDate might exist
      }));

      console.log(`[invoiceService/fetchFiltered] Fetched ${invoices.length} invoices from Firestore.`);

      // --- Update Cache ---
      cache[cacheKey] = {
        data: invoices,
        timestamp: Date.now()
      };
      console.log(`[invoiceService/fetchFiltered] Updated cache for key: ${cacheKey}`);

      return invoices;

    } catch (error) {
      console.error(`[invoiceService/fetchFiltered] Error fetching invoices for user ${userId}:`, error);
      // Check for specific Firestore errors (e.g., missing index)
      if (error.code === 'failed-precondition') {
        console.error("[invoiceService/fetchFiltered] Firestore index likely missing. Check the console error message for a link to create it.");
        throw new Error("Database query failed. A required index might be missing.");
      }
      throw error; // Re-throw other errors
    }
  },

  // Ensure fetchInvoices also respects forceRefresh if it exists or calls fetchFilteredInvoices
  async fetchInvoices(userId, options = {}) {
     // This function might just be a wrapper around fetchFilteredInvoices without filters
     console.log(`[invoiceService/fetchInvoices] Called with options:`, options);
     // Pass through filters (empty) and options to the filtered fetcher
     return this.fetchFilteredInvoices(userId, {}, options);
  },


  /**
   * Handle invoice data creation and archiving without PDF generation
   * @param {Object} options - Options for invoice processing
   * @param {Array} options.logs - The logs to include in the invoice
   * @param {Object} options.practice - The practice information
   * @param {Object} options.user - The user information
   * @param {string} [options.invoiceNumber] - Optional existing invoice number to reuse
   * @param {string} [options.invoiceId] - Optional Firestore document ID of the saved invoice
   * @param {boolean} [options.skipArchive] - Skip the archive step if true
   * @returns {Promise<Object>} - Returns the processed invoice data
   */
  async processInvoiceData(options) {
    const {
      logs, 
      practice, 
      user, 
      invoiceNumber: existingInvoiceNumber,
      invoiceId,
      skipArchive = false
    } = options;
    
    // Validate required parameters
    if (!logs || logs.length === 0) {
      throw new Error("No logs provided for invoice processing");
    }
    
    if (!practice) {
      throw new Error("Practice information is required");
    }
    
    if (!user) {
      throw new Error("User information is required");
    }
    
    try {
      // Use existing invoice number or get it from options
      const invoiceNumber = existingInvoiceNumber;
      
      // Archive the logs if needed
      const logIds = logs.map(log => log.id);
      
      // Only archive if there are log IDs and they appear to be valid
      if (!skipArchive && logIds.length > 0 && logIds.every(id => typeof id === 'string' && id.length > 0)) {
        // Only archive if we don't have an existing invoice number (meaning this is the first generation)
        if (!existingInvoiceNumber) {
          console.log(`Archiving ${logIds.length} logs with invoice number: ${invoiceNumber}`);
          await dailyLogService.archiveAndDeleteLogs(logIds, invoiceNumber, { skipPdfGeneration: true });
        } else {
          console.log('Skipping archive since this is a regeneration of an existing invoice');
        }
      } else if (skipArchive) {
        console.log('Skipping archive step as requested');
      }
      
      // Update the invoice document with metadata if we have an invoiceId
      if (invoiceId) {
        const invoiceRef = doc(db, 'invoices', invoiceId);
        await updateDoc(invoiceRef, {
          status: 'processed',
          lastUpdated: new Date()
        });
        console.log(`Updated invoice ${invoiceId} with processing info`);
      }
      
      // Return information about the invoice
      return {
        success: true,
        invoiceNumber,
        practice,
        logs,
        invoiceId
      };
      
    } catch (error) {
      console.error('Error processing invoice data:', error);
      throw error;
    }
  },

  /**
   * Format a standard email body for invoice emails
   * @param {Object} options - Email formatting options
   * @param {string} options.practiceName - The practice name
   * @param {number} options.totalAmount - The invoice total amount
   * @param {string} options.invoiceNumber - The invoice number
   * @param {string} options.filename - The PDF filename
   * @param {Object} options.profileData - User profile information
   * @param {Function} options.formatCurrency - Function to format currency values
   * @returns {Object} - Returns formatted email fields (to, subject, body)
   */
  formatInvoiceEmail(options) {
    const {
      practiceName,
      totalAmount,
      invoiceNumber,
      filename,
      profileData,
      formatCurrency
    } = options;
    
    // Get today's date formatted
    const todayFormatted = format(new Date(), 'dd MMM yyyy');
    
    // Extract treatment dates from logs if available or use today
    const treatmentDates = options.logs ? 
      options.logs.map(log => format(log.date, 'dd MMM yyyy')).join(', ') : 
      todayFormatted;
    
    // Generate email subject & body EXACTLY matching Monthly Invoice format
    const emailSubject = `Invoice ${invoiceNumber} from ${profileData?.name || 'Hygienist'} - ${todayFormatted}`;
    
    const emailBody = `⚠️ IMPORTANT: Please attach the PDF invoice named "${filename}" to this email before sending. ⚠️

Dear ${practiceName},

Please find details regarding Invoice #${invoiceNumber} attached to this email.

Invoice Summary:
- Invoice Number: ${invoiceNumber}
- Date: ${todayFormatted}
- Total Amount: ${formatCurrency(totalAmount)}
- Treatment Dates: ${treatmentDates}

Payment Information:
Bank: ${profileData?.bankName || ''}
Sort Code: ${profileData?.sortCode || ''}
Account Number: ${profileData?.accountNumber || ''}

Payment is due within 30 days from date of invoice.

Kind regards,
${profileData?.name || 'Hygienist'}`;

    return {
      subject: emailSubject,
      body: emailBody
    };
  }
};

/**
 * Handle invoice data creation and archiving without PDF generation
 * @param {Object} options - Options for invoice processing
 * @param {Array} options.logs - The logs to include in the invoice
 * @param {Object} options.practice - The practice information
 * @param {Object} options.user - The user information
 * @param {string} [options.invoiceNumber] - Optional existing invoice number to reuse
 * @param {string} [options.invoiceId] - Optional Firestore document ID of the saved invoice
 * @param {boolean} [options.skipArchive] - Skip the archive step if true
 * @returns {Promise<Object>} - Returns the processed invoice data
 */
export const processInvoiceData = async (options) => {
  const {
    logs, 
    practice, 
    user, 
    invoiceNumber: existingInvoiceNumber,
    invoiceId,
    skipArchive = false
  } = options;
  
  // Validate required parameters
  if (!logs || logs.length === 0) {
    throw new Error("No logs provided for invoice processing");
  }
  
  if (!practice) {
    throw new Error("Practice information is required");
  }
  
  if (!user) {
    throw new Error("User information is required");
  }
  
  try {
    // Use existing invoice number or get it from options
    const invoiceNumber = existingInvoiceNumber;
    
    // Archive the logs if needed
    const logIds = logs.map(log => log.id);
    
    // Only archive if there are log IDs and they appear to be valid
    if (!skipArchive && logIds.length > 0 && logIds.every(id => typeof id === 'string' && id.length > 0)) {
      // Only archive if we don't have an existing invoice number (meaning this is the first generation)
      if (!existingInvoiceNumber) {
        console.log(`Archiving ${logIds.length} logs with invoice number: ${invoiceNumber}`);
        await dailyLogService.archiveAndDeleteLogs(logIds, invoiceNumber, { skipPdfGeneration: true });
      } else {
        console.log('Skipping archive since this is a regeneration of an existing invoice');
      }
    } else if (skipArchive) {
      console.log('Skipping archive step as requested');
    }
    
    // Update the invoice document with metadata if we have an invoiceId
    if (invoiceId) {
      const invoiceRef = doc(db, 'invoices', invoiceId);
      await updateDoc(invoiceRef, {
        status: 'processed',
        lastUpdated: new Date()
      });
      console.log(`Updated invoice ${invoiceId} with processing info`);
    }
    
    // Return information about the invoice
    return {
      success: true,
      invoiceNumber,
      practice,
      logs,
      invoiceId
    };
    
  } catch (error) {
    console.error('Error processing invoice data:', error);
    throw error;
  }
};

/**
 * Format a standard email body for invoice emails
 * @param {Object} options - Email formatting options
 * @param {string} options.practiceName - The practice name
 * @param {number} options.totalAmount - The invoice total amount
 * @param {string} options.invoiceNumber - The invoice number
 * @param {string} options.filename - The PDF filename
 * @param {Object} options.profileData - User profile information
 * @param {Function} options.formatCurrency - Function to format currency values
 * @returns {Object} - Returns formatted email fields (to, subject, body)
 */
export const formatInvoiceEmail = (options) => {
  const {
    practiceName,
    totalAmount,
    invoiceNumber,
    filename,
    profileData,
    formatCurrency
  } = options;
  
  // Get today's date formatted
  const todayFormatted = format(new Date(), 'dd MMM yyyy');
  
  // Extract treatment dates from logs if available or use today
  const treatmentDates = options.logs ? 
    options.logs.map(log => format(log.date, 'dd MMM yyyy')).join(', ') : 
    todayFormatted;
  
  // Generate email subject & body EXACTLY matching Monthly Invoice format
  const emailSubject = `Invoice ${invoiceNumber} from ${profileData?.name || 'Hygienist'} - ${todayFormatted}`;
  
  const emailBody = `⚠️ IMPORTANT: Please attach the PDF invoice named "${filename}" to this email before sending. ⚠️

Dear ${practiceName},

Please find details regarding Invoice #${invoiceNumber} attached to this email.

Invoice Summary:
- Invoice Number: ${invoiceNumber}
- Date: ${todayFormatted}
- Total Amount: ${formatCurrency(totalAmount)}
- Treatment Dates: ${treatmentDates}

Payment Information:
Bank: ${profileData?.bankName || ''}
Sort Code: ${profileData?.sortCode || ''}
Account Number: ${profileData?.accountNumber || ''}

Payment is due within 30 days from date of invoice.

Kind regards,
${profileData?.name || 'Hygienist'}`;

  return {
    subject: emailSubject,
    body: emailBody
  };
};
